import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import { getRequest } from "../../utils/api";
import { logout } from "../../utils/auth";
import PeriodFilter, { periodRange } from "../../Component/PeriodFilter";
import Pill from "../../Component/Pill";
import TablePager from "../Users/components/TablePager";
import AffiliateLayout, { SETTLE_STATUS, affiliateName, clientName, inr } from "./components/AffiliateLayout";
import CommissionChart from "./components/CommissionChart";

// Interface for Stats (value + growth % vs previous period)
interface StatItem {
    value: number;
    growth: number;
}

interface CommissionStats {
    totalCommission: StatItem;
    pendingPayout: StatItem;
    totalPaidOut: StatItem;
    totalTransactions: StatItem;
    avgRate: StatItem;
}

const EMPTY_STAT: StatItem = { value: 0, growth: 0 };

const AffiliateComission = () => {
    const [searchParams] = useSearchParams();
    const urlSearch = searchParams.get("search") || "";

    const [commissionList, setCommissionList] = useState<any[]>([]);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(25);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCommissions, setTotalCommissions] = useState<number>(0);
    const [isLoader, setIsLoader] = useState<boolean>(true);

    // Stats & Chart Data
    const [stats, setStats] = useState<CommissionStats>({
        totalCommission: EMPTY_STAT,
        pendingPayout: EMPTY_STAT,
        totalPaidOut: EMPTY_STAT,
        totalTransactions: EMPTY_STAT,
        avgRate: EMPTY_STAT
    });
    const [dailyData, setDailyData] = useState<any[]>([]);
    const [tierData, setTierData] = useState<any[]>([]);
    const [statusCounts, setStatusCounts] = useState({ all: 0, pending: 0, paid: 0, failed: 0 });

    // Filters (a link from an affiliate drawer searches for that affiliate across all time)
    const [period, setPeriod] = useState(urlSearch ? "all" : "d30");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [searchText, setSearchText] = useState(urlSearch);
    const [searchQuery, setSearchQuery] = useState(urlSearch);
    const [statusFilter, setStatusFilter] = useState("");

    const [from, to] = useMemo(() => periodRange(period, customFrom, customTo), [period, customFrom, customTo]);

    // Fetch Commission Ledger (list, stats and charts follow the selected period)
    const getCommissionList = useCallback(async () => {
        const [f, t] = periodRange(period, customFrom, customTo);
        if (period === "custom" && (!f || !t)) return;
        const startDate = f ? f.format('YYYY-MM-DD') : "";
        const endDate = t ? t.format('YYYY-MM-DD') : "";
        try {
            setIsLoader(true);
            const response = await getRequest(
                `/affiliate/affiliate-commission-list?page=${page}&limit=${pageSize}&status=${statusFilter}&search=${encodeURIComponent(searchQuery)}&startDate=${startDate}&endDate=${endDate}`
            );

            if (response.status === "success") {
                setCommissionList(response.data);
                setTotalPages(response.pagination.totalPages || 1);
                setTotalCommissions(response.pagination.total ?? response.pagination.totalAffiliates);
                if (response.stats) setStats(response.stats);
                if (response.statusCounts) setStatusCounts(response.statusCounts);
                if (response.charts) {
                    setDailyData(response.charts.daily || []);
                    setTierData(response.charts.tiers || []);
                }
            } else if (response.status === "session_expired") {
                logout();
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error("Something went wrong loading commissions!");
        } finally {
            setIsLoader(false);
        }
    }, [page, pageSize, statusFilter, searchQuery, period, customFrom, customTo]);

    useEffect(() => {
        getCommissionList();
    }, [getCommissionList]);

    // Debounced Search
    useEffect(() => {
        const delay = setTimeout(() => {
            setSearchQuery(searchText);
            setPage(1);
        }, 500);
        return () => clearTimeout(delay);
    }, [searchText]);

    const changeStatus = (s: string) => { setStatusFilter(s); setPage(1); };

    const resetFilters = () => {
        setSearchText("");
        changeStatus("");
    };

    const chips: [string, string, number][] = [
        ["", "All", statusCounts.all],
        ["0", "Pending", statusCounts.pending],
        ["1", "Paid", statusCounts.paid],
        ["2", "Failed", statusCounts.failed],
    ];

    // All time has no previous period, so no growth %
    const isAll = period === "all";
    const tierTotal = tierData.reduce((s, t) => s + (Number(t.value) || 0), 0);

    return (
        <AffiliateLayout
            loading={isLoader}
            actions={
                <PeriodFilter
                    allTime
                    value={period}
                    onChange={(p) => { setPeriod(p); setPage(1); }}
                    from={customFrom}
                    to={customTo}
                    onRangeChange={(f, t) => { setCustomFrom(f); setCustomTo(t); setPage(1); }}
                />
            }
            stats={[
                { label: "Commission earned", value: inr(stats.totalCommission.value, 0), icon: "ri-exchange-dollar-line", delta: isAll ? "All time" : stats.totalCommission.growth },
                { label: "Pending payout", value: inr(stats.pendingPayout.value, 0), icon: "ri-time-line", delta: isAll ? "All time" : stats.pendingPayout.growth },
                { label: "Paid out", value: inr(stats.totalPaidOut.value, 0), icon: "ri-checkbox-circle-line", delta: isAll ? "All time" : stats.totalPaidOut.growth },
                { label: "Transactions", value: stats.totalTransactions.value, icon: "ri-file-list-3-line", delta: isAll ? "All time" : stats.totalTransactions.growth },
                { label: "Avg commission", value: inr(stats.avgRate.value), icon: "ri-scales-3-line", delta: isAll ? "All time" : stats.avgRate.growth },
            ]}
        >
            <div className="cx-grid g21" style={{ marginBottom: 12 }}>
                <div className="cx-card stretch">
                    <div className="cx-card-h">
                        <h6>Commission earned <span className="sub">· by date the client paid</span></h6>
                    </div>
                    <div className="cx-card-b">
                        <CommissionChart daily={dailyData} from={from} to={to} />
                    </div>
                </div>
                <div className="cx-card stretch">
                    <div className="cx-card-h">
                        <h6>By commission tier</h6>
                        <span className="fs12 muted mono">{inr(tierTotal, 0)}</span>
                    </div>
                    <div className="cx-card-b">
                        {tierData.length ? [...tierData].sort((a, b) => b.value - a.value).map(t => {
                            const share = tierTotal ? (Number(t.value) / tierTotal) * 100 : 0;
                            return (
                                <div key={t.name} style={{ marginBottom: 12 }}>
                                    <div className="cx-mini">
                                        <span>{t.name}</span>
                                        <span className="mono">{inr(t.value, 0)} <span className="muted">· {share.toFixed(0)}%</span></span>
                                    </div>
                                    <div className="cx-bar"><i style={{ width: `${share}%` }}></i></div>
                                </div>
                            );
                        }) : (
                            <div className="cx-empty" style={{ padding: "40px 10px" }}>No commission in this period.</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="cx-card">
                <div className="cx-toolbar">
                    <div className="cx-chips">
                        {chips.map(([k, l, n]) => (
                            <button type="button" key={l} className={`cx-chip ${statusFilter === k ? "on" : ""}`} onClick={() => changeStatus(k)}>
                                {l} <b>{(n || 0).toLocaleString("en-IN")}</b>
                            </button>
                        ))}
                    </div>
                    <div className="sp"></div>
                    <input
                        className="cx-input"
                        type="search"
                        placeholder="Search affiliate, client, commission ID…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                    <button type="button" className="cx-btn cx-btn-light cx-btn-sm" onClick={resetFilters}>
                        <i className="ri-refresh-line"></i>Reset
                    </button>
                </div>

                <div className="cx-tbl-wrap">
                    <table className="cx-tbl">
                        <thead>
                            <tr>
                                <th>Commission</th>
                                <th>Affiliate</th>
                                <th>Client</th>
                                <th>Tier</th>
                                <th className="r">Sale amount</th>
                                <th className="r">Commission</th>
                                <th>Status</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {commissionList && commissionList.length > 0 ? (
                                commissionList.map((item) => {
                                    const [tone, label] = SETTLE_STATUS[item.ac_status] || ["none", "Unknown"];
                                    const rate = item.ac_commission_rate ?? item.ac_commission_tier_id?.commission_amount;
                                    return (
                                        <tr key={item._id}>
                                            <td>
                                                <span className="mono">#{item.ac_unique_id || item.ac_id}</span>
                                                {item.ac_title && <div className="fs12 muted" style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{item.ac_title}</div>}
                                            </td>
                                            <td>
                                                <b style={{ fontWeight: 600 }}>{affiliateName(item.ac_affiliate_id)}</b>
                                                <div className="fs12 muted">{item.ac_affiliate_id?.affiliate_email || ""}</div>
                                            </td>
                                            <td>
                                                {clientName(item.ac_user_id)}
                                                <div className="fs12 muted">{item.ac_user_id?.user_email || ""}</div>
                                            </td>
                                            <td>
                                                {item.ac_commission_tier_id?.commission_title || <span className="muted">—</span>}
                                                {rate != null && <div className="fs12 muted mono">{rate}%</div>}
                                            </td>
                                            <td className="r mono">{inr(item.ac_sale_amount)}</td>
                                            <td className="r mono" style={{ fontWeight: 600 }}>{inr(item.ac_amount)}</td>
                                            <td>
                                                <Pill tone={tone}>{label}</Pill>
                                                {item.ac_status === 0 && item.ac_payout_id && <div className="fs12 muted">in a payout</div>}
                                            </td>
                                            <td className="fs12 muted">{moment(item.createdAt).format("DD MMM YYYY")}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={8}>
                                        <div className="cx-empty">
                                            <i className="ri-exchange-dollar-line"></i>
                                            {isLoader ? "Loading…" : <>No commissions match these filters. <button type="button" className="cx-link" onClick={resetFilters}>Reset filters</button></>}
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <TablePager
                    page={page}
                    totalPages={totalPages}
                    total={totalCommissions}
                    shown={commissionList.length}
                    pageSize={pageSize}
                    label="commissions"
                    note="Pending until its payout is marked paid"
                    setPage={setPage}
                    setPageSize={setPageSize}
                />
            </div>
        </AffiliateLayout>
    );
};

export default AffiliateComission;
